import React, { useState, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Input,
  Card,
  CardHeader,
  CardBody,
} from "reactstrap"
import {
  addMutualFundFileUploadStart,
  addMutualFundFileUploadReset,
  getMutualFundBulkUploads,
  revertMutualFundBulkUpload,
} from "store/actions"
import Loader from "common/Loader"
import { toast } from "react-toastify"
import moment from "moment"
import TableContainer from "components/Common/TableContainer"
import templateFile from "./bulkMutualFundFormat/Mutual_Fund_BulkUpload_Template.xlsx";

const MutualFundBulkUpload = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {
    uploaded,
    uploading,
    bulkupload,
    successMsg,
    error,
    bulkMutualFundList,
  } = useSelector(state => state.fileUploadReducer)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedBulk, setSelectedBulk] = useState(null)
  const [revertModal, setRevertModal] = useState(false)

  const columns = useMemo(
    () => [
      {
        Header: "Sr No",
        accessor: (row, i) => i + 1,
        filterable: true,
        Cell: ({ row }) => <>{row.index + 1}</>,
      },
      {
        Header: "File Name",
        accessor: "filename",
        Cell: cellProps => (
          <>
            {cellProps.row.original.filename
              ? cellProps.row.original.filename.substring(
                  cellProps.row.original.filename.lastIndexOf("/") + 1
                )
              : ""}
          </>
        ),
      },
      {
        Header: "Upload Name",
        accessor: "upload_name",
        Cell: cellProps => {
            let name = cellProps.row.original.upload_name || "";

            // Remove timestamp (_173738383838)
            name = name.replace(/_\d+$/, "");

            // Optional: make it user-friendly (replace underscores with spaces)
            name = name.replace(/_/g, " ");

            // Capitalize words (optional professional UI)
            name = name.replace(/\b\w/g, c => c.toUpperCase());

            return <>{name}</>;
        },
        },
      {
        Header: "Total Records",
        accessor: "total_records",
      },
      {
        Header: "Inserted Records",
        accessor: "inserted_records",
      },
      {
        Header: "Failed Records",
        accessor: "failed_records",
      },
      {
        Header: "Duplicate Records",
        accessor: "duplicate_records",
      },
      {
        Header: "Status",
        accessor: "status",
      },
      {
        Header: "Date",
        accessor: "createdAt",
        Cell: cellProps => (
          <>
            {cellProps.row?.original?.createdAt
              ? moment(cellProps.row.original.createdAt).format("DD/MM/YYYY")
              : ""}
          </>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: cellProps => (
          <Button
            color={
              cellProps.row.original.status !== "Reverted"
                ? "primary"
                : "secondary"
            }
            style={{ minWidth: "130px", fontSize: "10px" }}
            disabled={cellProps.row.original.status === "Reverted"}
            onClick={() => {
              setSelectedBulk(cellProps.row.original)
              setRevertModal(true)
            }}
          >
            Revert
          </Button>
        ),
      },
    ],
    []
  )

  const toggleModal = () => {
    setModalOpen(!modalOpen)
    dispatch(addMutualFundFileUploadReset())
  }

  const handleFileChange = e => {
    const file = e.target.files[0]
    if (file) {
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "text/csv"
      ) {
        setSelectedFile(file)
      } else {
        toast.error("Please select a valid Excel or CSV file.")
      }
    }
  }

  const handleFileUpload = e => {
    e.preventDefault()

    if (!selectedFile) {
      toast.error("Please select a file first")
      return
    }

    const formData = new FormData()
    formData.append("upload_name", "mutual_fund_bulk") // 🔥 matches backend
    formData.append("MutualFundFile", selectedFile) // 🔥 IMPORTANT KEY

    dispatch(addMutualFundFileUploadStart(formData))
  }

  const HandleRevertBulk = () => {
        dispatch(revertMutualFundBulkUpload({ 
            bulkUploadId: selectedBulk.id 
        }))
    }

  useEffect(() => {
    if (uploaded) {
      dispatch(getMutualFundBulkUploads())
    }
  }, [uploaded, dispatch])

  useEffect(() => {
    dispatch(getMutualFundBulkUploads())
  }, [dispatch])

  useEffect(() => {
    if (successMsg) {
      toast.success(successMsg)
    }
  }, [successMsg])

  useEffect(() => {
    if (error) {
      toast.error(error)
    }
  }, [error])

  return (
    <div className="page-content">
      <div className="d-flex justify-content-between align-items-center">
        <h2>Mutual Fund Bulk Upload</h2>

        <div>
          <Button color="primary" onClick={toggleModal}>
            New Bulk Upload
          </Button>

          <button
            onClick={() => navigate(-1)}
            className="btn btn-light ms-2"
          >
            Back
          </button>
        </div>
      </div>

      {/* Table */}
      <TableContainer
        columns={columns}
        Loading={uploading}
        data={bulkMutualFundList}
        isGlobalFilter={false}
        isAddOptions={false}
        isJobListGlobalFilter={false}
        isPagination={true}
        iscustomPageSizeOptions={true}
        isShowingPageLength={true}
        customPageSize={20}
        tableClass="table-bordered align-middle nowrap mt-2"
        paginationDiv="col-sm-12 col-md-7"
        pagination="pagination justify-content-end pagination-rounded"
      />

      {/* Upload Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Upload Mutual Fund File</ModalHeader>

        <ModalBody>
          <Input type="file" accept=".xlsx,.csv" onChange={handleFileChange} />

          {uploading && <Loader />}

          {bulkupload && Object.keys(bulkupload).length > 0 && (
            <>
              <Card className="mx-2 mt-3">
                <CardHeader>Uploading Status:</CardHeader>
                <CardBody style={{ backgroundColor: "#ffeeba" }}>
                  {bulkupload.message || ""}
                </CardBody>
              </Card>

              <Card className="mx-2 mt-2">
                <CardHeader>Duplicate Records:</CardHeader>
                <CardBody style={{ backgroundColor: "#d4edda" }}>
                  <ul>
                    {bulkupload.duplicateCustomerMsg &&
                    bulkupload.duplicateCustomerMsg.length > 0 ? (
                      bulkupload.duplicateCustomerMsg.map((msg, index) => (
                        <li key={index} style={{ color: "blue" }}>
                          {msg}
                        </li>
                      ))
                    ) : (
                      <div>No Duplicate Records</div>
                    )}
                  </ul>
                </CardBody>
              </Card>

              <Card className="mx-2 mt-2">
                <CardHeader style={{ backgroundColor: "#f8d7da" }}>
                  Failed Records:
                </CardHeader>
                <CardBody>
                  <ul>
                    {bulkupload.FailedCustomerMsg &&
                    bulkupload.FailedCustomerMsg.length > 0 ? (
                      bulkupload.FailedCustomerMsg.map((msg, index) => (
                        <li key={index} style={{ color: "red" }}>
                          {msg}
                        </li>
                      ))
                    ) : (
                      <div>No Failed Records</div>
                    )}
                  </ul>
                </CardBody>
              </Card>
            </>
          )}
        </ModalBody>

        <ModalFooter className="d-flex justify-content-between align-items-center">
          {/* Download Template Button (Blue) */}
          <a
            href={templateFile}
            download="Mutual_Fund_BulkUpload_Template.xlsx"
            className="btn btn-primary"
            style={{ minWidth: "160px" }}
          >
            Download Template
          </a>

          {/* Right Side Buttons with Proper Gap */}
          <div className="d-flex gap-2">
            <Button color="primary" onClick={handleFileUpload}>
              Upload
            </Button>
            <Button color="secondary" onClick={toggleModal}>
              Cancel
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Revert Modal */}
      <Modal isOpen={revertModal} toggle={() => setRevertModal(false)}>
        <ModalHeader toggle={() => setRevertModal(false)}>
          Do you want to revert this bulk upload?
        </ModalHeader>

        <ModalBody>
          <table className="w-100">
            <tbody>
              <tr>
                <td className="fw-bold">File Name</td>
                <td>
                  :{" "}
                  {selectedBulk && selectedBulk.filename
                    ? selectedBulk.filename
                    : ""}
                </td>
              </tr>
              <tr>
                <td className="fw-bold">Upload Name</td>
                <td>
                  :{" "}
                  {selectedBulk && selectedBulk.upload_name
                    ? selectedBulk.upload_name
                    : ""}
                </td>
              </tr>
            </tbody>
          </table>
        </ModalBody>

        <ModalFooter>
          <Button color="danger" onClick={HandleRevertBulk}>
            Revert Now
          </Button>
          <Button color="primary" onClick={() => setRevertModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default MutualFundBulkUpload